import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { HabitatIntactnessExplainer } from '@/components/NCExplainers/HabitatIntactnessExplainer';
import { COMPLIANCE_LOGOS_HABITAT_INTACTNESS } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { HabitatIntactnessThreatsBreakdownGraphTile } from './GraphTile';
import { HabitatIntactnessThreatsBreakdownStatsTile } from './StatsTile';

export const HabitatIntactnessThreatsBreakdown = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness')}>
      <HabitatIntactnessThreatsBreakdownCard />
    </PlotCardWrapper>
  );
};

const HabitatIntactnessThreatsBreakdownCard = () => {
  const { t } = useTranslation();

  // TODO: MVP-8306 - merge into factNames when factlabel grouping bug is fixed
  const scalarFactNames = {
    latestAnalysis: R1FactType.threat_breakdown_latest_total,
    trend: R1FactType.threat_breakdown_total_previous_5y_change,
    baseline: R1FactType.threat_breakdown_baseline_total,
  };
  const graphFactNames = {
    graph: R1FactType.threat_breakdown_total_historic_graph,
  };

  const scalarFacts = usePlotFacts(Object.values(scalarFactNames));
  const graphFacts = usePlotFacts(Object.values(graphFactNames));

  const graphData = getFactFromFactArray<GraphFact[]>(graphFacts, graphFactNames.graph)?.value;
  const hasChartData = !isNullish(graphData);

  const latestFact = getFactFromFactArray<number>(scalarFacts, scalarFactNames.latestAnalysis);
  const trendFact = getFactFromFactArray<number>(scalarFacts, scalarFactNames.trend);
  const baselineFact = getFactFromFactArray<number>(scalarFacts, scalarFactNames.baseline);

  const hasStatsData = !isNullish(latestFact?.value) || !isNullish(baselineFact?.value) || !isNullish(trendFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<HabitatIntactnessExplainer />} data-testid='habitat-intactness-threats-breakdown-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdown')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <HabitatIntactnessThreatsBreakdownGraphTile data={graphData} />
        <HabitatIntactnessThreatsBreakdownStatsTile
          latestFact={latestFact}
          baselineFact={baselineFact}
          trendFact={trendFact}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_HABITAT_INTACTNESS} />
    </NCCard>
  );
};
